import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { supabase } from '@/lib/supabase/supabaseClient'
import { createFileRoute } from '@tanstack/react-router'
import type { Factor, PasskeyListItem } from '@supabase/supabase-js'
import {
  Check,
  Copy,
  KeyRound,
  MoreHorizontal,
  Pencil,
  Plus,
  ShieldCheck,
  Trash2,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute(
  '/_auth/_dashboard/settings/_layout/security',
)({
  component: SecurityPage,
})

const MFA_ENABLED = false

function SecurityPage() {
  const [passkeys, setPasskeys] = useState<PasskeyListItem[]>([])
  const [mfaFactors, setMfaFactors] = useState<Factor<'totp', 'verified'>[]>(
    [],
  )
  const [loading, setLoading] = useState(true)
  const [mfaLoading, setMfaLoading] = useState(true)
  const [registering, setRegistering] = useState(false)
  const [mfaEnroll, setMfaEnroll] = useState<MfaEnrollment | null>(null)
  const [mfaCode, setMfaCode] = useState('')
  const [mfaBusy, setMfaBusy] = useState(false)
  const [mfaDialogOpen, setMfaDialogOpen] = useState(false)
  const [mfaShowSecret, setMfaShowSecret] = useState(false)
  const [mfaSecretCopied, setMfaSecretCopied] = useState(false)
  const [deletingMfaFactor, setDeletingMfaFactor] =
    useState<Factor<'totp', 'verified'> | null>(null)
  const [savingMfaFactorId, setSavingMfaFactorId] = useState<string | null>(
    null,
  )
  const [savingPasskeyId, setSavingPasskeyId] = useState<string | null>(null)
  const [deletingPasskey, setDeletingPasskey] =
    useState<PasskeyListItem | null>(null)
  const [editingPasskeyId, setEditingPasskeyId] = useState<string | null>(null)
  const [friendlyName, setFriendlyName] = useState('')
  const [passkeyApiAvailable, setPasskeyApiAvailable] = useState(true)

  const sortedPasskeys = useMemo(
    () =>
      [...passkeys].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
    [passkeys],
  )

  const sortedMfaFactors = useMemo(
    () =>
      [...mfaFactors].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
    [mfaFactors],
  )

  const loadPasskeys = async () => {
    const passkeyApi = getPasskeyApi()
    if (!passkeyApi) {
      setPasskeyApiAvailable(false)
      setPasskeys([])
      setLoading(false)
      return
    }

    setLoading(true)
    const { data, error } = await passkeyApi.list()
    if (error) {
      toast.error('Could not load passkeys')
      console.error('Error loading passkeys', error)
      setLoading(false)
      return
    }
    setPasskeys(data ?? [])
    setLoading(false)
  }

  const loadMfaFactors = async () => {
    setMfaLoading(true)
    const { data, error } = await supabase.auth.mfa.listFactors()
    if (error) {
      toast.error('Could not load MFA devices')
      console.error('Error loading MFA factors', error)
      setMfaLoading(false)
      return
    }
    setMfaFactors(data.totp ?? [])
    setMfaLoading(false)
  }

  useEffect(() => {
    void loadPasskeys()
    if (MFA_ENABLED) {
      void loadMfaFactors()
    }
  }, [])

  const registerPasskey = async () => {
    if (typeof supabase.auth.registerPasskey !== 'function') {
      setPasskeyApiAvailable(false)
      toast.error('Passkey support is not available in this browser session')
      return
    }

    setRegistering(true)
    const { data, error } = await supabase.auth.registerPasskey()
    if (error) {
      toast.error(getPasskeyErrorMessage(error))
      console.error('Error registering passkey', error)
      setRegistering(false)
      return
    }
    if (data) {
      setPasskeys((current) => [
        ...current.filter((passkey) => passkey.id !== data.id),
        data,
      ])
    }
    toast.success('Passkey added')
    setRegistering(false)
  }

  const startEditing = (passkey: PasskeyListItem) => {
    setEditingPasskeyId(passkey.id)
    setFriendlyName(getPasskeyName(passkey))
  }

  const cancelEditing = () => {
    setEditingPasskeyId(null)
    setFriendlyName('')
  }

  const updatePasskey = async (passkeyId: string) => {
    const passkeyApi = getPasskeyApi()
    if (!passkeyApi) {
      setPasskeyApiAvailable(false)
      toast.error('Passkey support is not available in this browser session')
      return
    }

    const nextName = friendlyName.trim()
    if (!nextName) {
      toast.error('Passkey name is required')
      return
    }
    setSavingPasskeyId(passkeyId)
    const { data, error } = await passkeyApi.update({
      passkeyId,
      friendlyName: nextName,
    })
    if (error) {
      toast.error('Could not rename passkey')
      console.error('Error renaming passkey', error)
      setSavingPasskeyId(null)
      return
    }
    if (data) {
      setPasskeys((current) =>
        current.map((passkey) => (passkey.id === data.id ? data : passkey)),
      )
    }
    toast.success('Passkey renamed')
    setSavingPasskeyId(null)
    cancelEditing()
  }

  const deletePasskey = async () => {
    if (!deletingPasskey) return
    const passkeyApi = getPasskeyApi()
    if (!passkeyApi) {
      setPasskeyApiAvailable(false)
      toast.error('Passkey support is not available in this browser session')
      return
    }

    setSavingPasskeyId(deletingPasskey.id)
    const { error } = await passkeyApi.delete({
      passkeyId: deletingPasskey.id,
    })
    if (error) {
      toast.error('Could not delete passkey')
      console.error('Error deleting passkey', error)
      setSavingPasskeyId(null)
      return
    }
    setPasskeys((current) =>
      current.filter((passkey) => passkey.id !== deletingPasskey.id),
    )
    toast.success('Passkey deleted')
    setSavingPasskeyId(null)
    setDeletingPasskey(null)
  }

  const startMfaEnrollment = async () => {
    setMfaBusy(true)
    setMfaCode('')
    setMfaShowSecret(false)
    setMfaSecretCopied(false)
    setMfaDialogOpen(true)

    await removeUnverifiedTotpFactors()
    const { data, error } = await enrollTotpFactor()

    if (error && isDuplicateMfaFriendlyNameError(error)) {
      await removeUnverifiedTotpFactors()
      const retry = await enrollTotpFactor()
      if (!retry.error) {
        setMfaEnroll({
          factorId: retry.data.id,
          qrCode: retry.data.totp.qr_code,
          secret: retry.data.totp.secret,
        })
        setMfaBusy(false)
        return
      }
    }

    if (error) {
      toast.error('Could not start MFA setup')
      console.error('Error enrolling MFA factor', error)
      setMfaDialogOpen(false)
      setMfaBusy(false)
      return
    }

    setMfaEnroll({
      factorId: data.id,
      qrCode: data.totp.qr_code,
      secret: data.totp.secret,
    })
    setMfaBusy(false)
  }

  const enrollTotpFactor = () =>
    supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: 'Authenticator app',
    })

  const removeUnverifiedTotpFactors = async () => {
    const { data, error } = await supabase.auth.mfa.listFactors()
    if (error) {
      console.error('Error loading MFA factors before enrolment', error)
      return
    }

    const unverifiedTotpFactors = data.all.filter(
      (factor) => factor.factor_type === 'totp' && factor.status === 'unverified',
    )

    await Promise.all(
      unverifiedTotpFactors.map((factor) =>
        supabase.auth.mfa.unenroll({ factorId: factor.id }),
      ),
    )
  }

  const cancelMfaEnrollment = async () => {
    const factorId = mfaEnroll?.factorId
    setMfaEnroll(null)
    setMfaCode('')
    setMfaShowSecret(false)
    setMfaSecretCopied(false)
    setMfaDialogOpen(false)
    if (!factorId) return
    const { error } = await supabase.auth.mfa.unenroll({ factorId })
    if (error) {
      console.error('Error cleaning up unverified MFA factor', error)
    }
  }

  const verifyMfaEnrollment = async () => {
    if (!mfaEnroll || mfaCode.length !== 6) return

    setMfaBusy(true)
    const { data: challengeData, error: challengeError } =
      await supabase.auth.mfa.challenge({ factorId: mfaEnroll.factorId })

    if (challengeError) {
      toast.error('Could not verify MFA code')
      console.error('Error challenging MFA factor', challengeError)
      setMfaBusy(false)
      return
    }

    const { error: verifyError } = await supabase.auth.mfa.verify({
      factorId: mfaEnroll.factorId,
      challengeId: challengeData.id,
      code: mfaCode,
    })

    if (verifyError) {
      toast.error('Invalid MFA code')
      console.error('Error verifying MFA factor', verifyError)
      setMfaBusy(false)
      return
    }

    toast.success('MFA enabled')
    setMfaEnroll(null)
    setMfaCode('')
    setMfaShowSecret(false)
    setMfaSecretCopied(false)
    setMfaDialogOpen(false)
    setMfaBusy(false)
    await loadMfaFactors()
  }

  const handleMfaDialogOpenChange = (open: boolean) => {
    if (open) {
      setMfaDialogOpen(true)
      return
    }

    if (mfaBusy) return
    void cancelMfaEnrollment()
  }

  const copyMfaSecret = async () => {
    if (!mfaEnroll?.secret) return
    try {
      await navigator.clipboard.writeText(mfaEnroll.secret)
      setMfaSecretCopied(true)
      window.setTimeout(() => setMfaSecretCopied(false), 2000)
      toast.success('MFA setup code copied')
    } catch (error) {
      toast.error('Could not copy MFA setup code')
      console.error('Error copying MFA setup code', error)
    }
  }

  const deleteMfaFactor = async () => {
    if (!deletingMfaFactor) return
    setSavingMfaFactorId(deletingMfaFactor.id)
    const { error } = await supabase.auth.mfa.unenroll({
      factorId: deletingMfaFactor.id,
    })
    if (error) {
      toast.error('Could not remove MFA device')
      console.error('Error deleting MFA factor', error)
      setSavingMfaFactorId(null)
      return
    }

    setMfaFactors((current) =>
      current.filter((factor) => factor.id !== deletingMfaFactor.id),
    )
    await supabase.auth.refreshSession()
    toast.success('MFA device removed')
    setSavingMfaFactorId(null)
    setDeletingMfaFactor(null)
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Security</h3>
        <p className="text-sm text-muted-foreground">
          Manage passkeys and multi-factor authentication.
        </p>
      </div>
      <Separator />

      {MFA_ENABLED && (
        <>
          <section className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                <h4 className="text-sm font-medium">
                  Multi-factor authentication
                </h4>
                <p className="text-sm text-muted-foreground">
                  Require a 6 digit authenticator app code after sign-in.
                </p>
              </div>
              <Button
                onClick={startMfaEnrollment}
                disabled={mfaBusy || mfaLoading || !!mfaEnroll}
              >
                <Plus className="mr-2 h-4 w-4" />
                {mfaBusy && !mfaEnroll ? 'Starting...' : 'Add MFA device'}
              </Button>
            </div>

            {mfaLoading ? (
              <div className="space-y-3 rounded-md border p-4">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-64" />
              </div>
            ) : sortedMfaFactors.length ? (
              <div className="divide-y rounded-md border">
                {sortedMfaFactors.map((factor) => (
                  <div
                    key={factor.id}
                    className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex min-w-0 gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted">
                        <ShieldCheck className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 space-y-1">
                        <p className="truncate text-sm font-medium">
                          {getMfaName(factor)}
                        </p>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          <span>Added {formatDate(factor.created_at)}</span>
                          <span>Verified</span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeletingMfaFactor(factor)}
                      disabled={savingMfaFactorId !== null}
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-md border border-dashed p-6">
                <div className="flex max-w-md flex-col gap-2">
                  <ShieldCheck className="h-8 w-8 text-muted-foreground" />
                  <h4 className="text-sm font-medium">No MFA devices added</h4>
                  <p className="text-sm text-muted-foreground">
                    Add an authenticator app to require a second factor when you
                    sign in.
                  </p>
                </div>
              </div>
            )}
          </section>

          <Separator />
        </>
      )}

      <section className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <h4 className="text-sm font-medium">Passkeys</h4>
            <p className="text-sm text-muted-foreground">
              Add a passkey from this device or remove passkeys you no longer
              use.
            </p>
          </div>
          <Button
            onClick={registerPasskey}
            disabled={registering || loading || !passkeyApiAvailable}
          >
            <Plus className="mr-2 h-4 w-4" />
            {registering ? 'Adding...' : 'Add passkey'}
          </Button>
        </div>

        {!passkeyApiAvailable ? (
          <div className="rounded-md border border-dashed p-6">
            <div className="flex max-w-md flex-col gap-2">
              <KeyRound className="h-8 w-8 text-muted-foreground" />
              <h4 className="text-sm font-medium">Passkeys unavailable</h4>
              <p className="text-sm text-muted-foreground">
                Refresh the app after the Supabase client update, or restart the
                dev server with fresh Vite dependencies.
              </p>
            </div>
          </div>
        ) : loading ? (
          <div className="space-y-3 rounded-md border p-4">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : sortedPasskeys.length ? (
          <div className="divide-y rounded-md border">
            {sortedPasskeys.map((passkey) => {
              const isEditing = editingPasskeyId === passkey.id
              const isSaving = savingPasskeyId === passkey.id

              return (
                <div
                  key={passkey.id}
                  className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted">
                      <KeyRound className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 space-y-1">
                      {isEditing ? (
                        <div className="space-y-2">
                          <Label htmlFor={`passkey-${passkey.id}`}>
                            Passkey name
                          </Label>
                          <Input
                            id={`passkey-${passkey.id}`}
                            value={friendlyName}
                            maxLength={120}
                            onChange={(event) =>
                              setFriendlyName(event.target.value)
                            }
                          />
                        </div>
                      ) : (
                        <>
                          <p className="truncate text-sm font-medium">
                            {getPasskeyName(passkey)}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span>Added {formatDate(passkey.created_at)}</span>
                            {passkey.last_used_at && (
                              <span>
                                Last used {formatDate(passkey.last_used_at)}
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">
                    {isEditing ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={cancelEditing}
                          disabled={isSaving}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => updatePasskey(passkey.id)}
                          disabled={isSaving}
                        >
                          {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                      </>
                    ) : (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={savingPasskeyId !== null}
                            aria-label={`Manage ${getPasskeyName(passkey)}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => startEditing(passkey)}
                          >
                            <Pencil className="h-4 w-4" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            variant="destructive"
                            onClick={() => setDeletingPasskey(passkey)}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="rounded-md border border-dashed p-6">
            <div className="flex max-w-md flex-col gap-2">
              <KeyRound className="h-8 w-8 text-muted-foreground" />
              <h4 className="text-sm font-medium">No passkeys added</h4>
              <p className="text-sm text-muted-foreground">
                Add a passkey to sign in with your device unlock, password
                manager, or security key.
              </p>
            </div>
          </div>
        )}
      </section>

      <AlertDialog
        open={!!deletingPasskey}
        onOpenChange={(open) => {
          if (!open && !savingPasskeyId) setDeletingPasskey(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete passkey?</AlertDialogTitle>
            <AlertDialogDescription>
              This passkey will no longer be able to sign in to your Trainrr
              account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!savingPasskeyId}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={deletePasskey}
              disabled={!!savingPasskeyId}
            >
              {savingPasskeyId ? 'Deleting...' : 'Delete passkey'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={mfaDialogOpen}
        onOpenChange={handleMfaDialogOpenChange}
        analyticsName="mfa-enrolment"
      >
        <DialogContent
          className="max-w-xl gap-0 p-0"
          showCloseButton={!mfaBusy}
        >
          <DialogHeader>
            <DialogTitle className="px-6 pt-6 text-xl">
              Connect your authenticator app
            </DialogTitle>
            <DialogDescription className="sr-only">
              Set up multi-factor authentication using a QR code or manual setup
              key.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-5 border-t px-6 py-5">
            {mfaEnroll ? (
              <div className="space-y-5">
                <div className="space-y-3">
                  <p className="text-base leading-relaxed">
                    <span className="font-semibold">Step 1:</span>{' '}
                    {mfaShowSecret
                      ? 'Enter the secret code below in your authenticator app, then enter the 6-digit code from the app.'
                      : 'Scan the QR code using your authenticator app, then enter the 6-digit code from the app.'}
                  </p>

                  <div className="rounded-md border p-5">
                    {mfaShowSecret ? (
                      <div className="space-y-3 text-center">
                        <button
                          type="button"
                          className="block w-full break-all rounded-md border bg-muted px-3 py-2 text-left text-sm font-medium tracking-wide transition-colors hover:bg-muted/80 sm:text-center"
                          onClick={copyMfaSecret}
                          disabled={mfaBusy}
                        >
                          {mfaEnroll.secret}
                        </button>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={copyMfaSecret}
                          disabled={mfaBusy}
                        >
                          {mfaSecretCopied ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                          {mfaSecretCopied ? 'Copied' : 'Copy code'}
                        </Button>
                        <div>
                          <Button
                            type="button"
                            variant="link"
                            className="h-auto px-0 text-sm"
                            onClick={() => setMfaShowSecret(false)}
                            disabled={mfaBusy}
                          >
                            Show QR code instead
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3 text-center">
                        <div className="flex justify-center">
                          <div className="bg-white p-3">
                            <img
                              src={mfaEnroll.qrCode}
                              alt="Authenticator app QR code"
                              className="h-44 w-44"
                            />
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="link"
                          className="h-auto px-0 text-sm"
                          onClick={() => setMfaShowSecret(true)}
                          disabled={mfaBusy}
                        >
                          Trouble scanning?
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-base font-normal">
                    <span className="font-semibold">Step 2:</span> Enter your
                    6-digit code
                  </Label>
                  <Input
                    value={mfaCode}
                    onChange={(event) =>
                      setMfaCode(normaliseMfaCode(event.target.value))
                    }
                    disabled={mfaBusy}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="Enter your 6-digit code"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3 py-5">
                <Skeleton className="mx-auto h-36 w-36" />
                <Skeleton className="h-4 w-64" />
                <Skeleton className="h-10 w-full max-w-sm" />
              </div>
            )}
          </div>

          <DialogFooter className="px-6 pb-5">
            <Button
              type="button"
              variant="outline"
              onClick={cancelMfaEnrollment}
              disabled={mfaBusy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={verifyMfaEnrollment}
              disabled={mfaBusy || !mfaEnroll || mfaCode.length !== 6}
            >
              {mfaBusy ? 'Verifying...' : 'Verify'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deletingMfaFactor}
        onOpenChange={(open) => {
          if (!open && !savingMfaFactorId) setDeletingMfaFactor(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove MFA device?</AlertDialogTitle>
            <AlertDialogDescription>
              You will no longer be asked for codes from this authenticator app
              after sign-in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!savingMfaFactorId}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={deleteMfaFactor}
              disabled={!!savingMfaFactorId}
            >
              {savingMfaFactorId ? 'Removing...' : 'Remove MFA device'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

type MfaEnrollment = {
  factorId: string
  qrCode: string
  secret: string
}

function getMfaName(factor: Pick<Factor<'totp', 'verified'>, 'friendly_name' | 'id'>) {
  return factor.friendly_name?.trim() || `Authenticator ${factor.id.slice(0, 8)}`
}

function getPasskeyName(passkey: Pick<PasskeyListItem, 'friendly_name' | 'id'>) {
  return passkey.friendly_name?.trim() || `Passkey ${passkey.id.slice(0, 8)}`
}

function getPasskeyApi() {
  return 'passkey' in supabase.auth ? supabase.auth.passkey : undefined
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value))
}

function normaliseMfaCode(value: string) {
  return value.replace(/\D/g, '').slice(0, 6)
}

function isDuplicateMfaFriendlyNameError(error: { message?: string }) {
  return error.message?.toLowerCase().includes('friendly name') ?? false
}

function getPasskeyErrorMessage(error: { code?: string; message?: string }) {
  if (error.code === 'passkey_disabled') {
    return 'Passkeys are not enabled for this project'
  }
  if (error.code === 'too_many_passkeys') {
    return 'This account has reached the passkey limit'
  }
  if (error.code === 'webauthn_credential_exists') {
    return 'This passkey is already registered'
  }
  return error.message || 'Could not add passkey'
}
